import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api.js';

const peso = (v) => `₱${Number(v ?? 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const STATUS = {
  completed: ['Completed', 'badge-available'],
  audit_hold: ['Under audit', 'badge-faulted'],
  rejected: ['Not started', 'bg-secondary'],
  charging: ['Charging', 'text-bg-primary'],
  stopping: ['Stopping', 'text-bg-primary'],
  starting: ['Starting', 'text-bg-primary'],
};

/** "Report a problem" form for one billed session: files a dispute with Customer Care. */
export function ReportProblem({ cdrNumber, onFiled, onCancel }) {
  const [statement, setStatement] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.openDispute({ cdrNumber, statement: statement.trim() });
      onFiled();
    } catch (err) {
      setError(err.details?.map?.((d) => d.message).join(' · ') || err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-2 p-3 border rounded bg-light" data-testid="report-problem-form">
      <label className="form-label small fw-bold" htmlFor={`dispute-${cdrNumber}`}>What went wrong with this session?</label>
      <textarea id={`dispute-${cdrNumber}`} className="form-control mb-2" rows={3} maxLength={1000} value={statement} onChange={(e) => setStatement(e.target.value)} placeholder="For example: charging stopped early but I was billed for the full session." />
      {error && <div className="alert alert-danger py-2 small" role="alert">{error}</div>}
      <div className="d-flex flex-wrap gap-2">
        <button type="submit" className="btn btn-primary" disabled={busy || statement.trim().length < 5}>{busy ? 'Sending…' : 'Send to Customer Care'}</button>
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
      <div className="form-text">Customer Care reviews the charger&apos;s meter record and replies with a credit or an explanation.</div>
    </form>
  );
}

/** The driver's past sessions with receipt and dispute actions. Hidden if history is unavailable. */
export default function RecentSessions() {
  const [rows, setRows] = useState(null); // null = not loaded / unavailable
  const [reporting, setReporting] = useState(null);
  const [filed, setFiled] = useState({});

  useEffect(() => {
    let live = true;
    api.listMySessions(20).then((list) => live && setRows(Array.isArray(list) ? list : [])).catch(() => live && setRows(null));
    return () => { live = false; };
  }, []);

  return (
    <div className="container-fluid recent-sessions" id="recent-sessions">
      {rows && rows.length > 0 && (
        <div className="card p-3 shadow-sm border bg-white">
          <h5 className="fw-bold mb-3" style={{ color: '#005293' }}><i className="fa fa-clock-rotate-left me-2" aria-hidden="true" />Recent sessions</h5>
          {rows.map((s) => {
            const [label, cls] = STATUS[s.status] || [s.status, 'bg-secondary'];
            const disputed = s.hasDispute || filed[s.id];
            return (
              <div key={s.id} className="recent-session" data-session-id={s.id}>
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div className="recent-session-main">
                    <div className="fw-semibold">{s.siteName || s.chargePoint || 'Charging session'}</div>
                    <div className="text-muted small">{when(s.startedAt || s.stoppedAt)} &middot; {s.connectorType || ''} &middot; {s.sessionCode}</div>
                  </div>
                  <div className="text-end flex-shrink-0">
                    <div className="fw-bold">{s.total != null ? peso(s.total) : '—'}</div>
                    <div className="text-muted small">{Number(s.energyKwh ?? 0).toFixed(2)} kWh</div>
                  </div>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                  <span className={`badge ${cls}`}>{label}</span>
                  <Link to={`/live-charging-telemetry?session_id=${s.id}`} className="btn btn-sm btn-outline-primary">View receipt</Link>
                  {disputed && <span className="badge bg-warning text-dark"><i className="fa fa-flag me-1" aria-hidden="true" />Dispute filed</span>}
                  {!disputed && s.cdrNumber && reporting !== s.id && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setReporting(s.id)}>Report a problem</button>
                  )}
                </div>
                {reporting === s.id && (
                  <ReportProblem cdrNumber={s.cdrNumber} onCancel={() => setReporting(null)} onFiled={() => { setFiled((f) => ({ ...f, [s.id]: true })); setReporting(null); }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
